import * as React from 'react'

import { Box, List, Row, Title } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ExpensesSignedDocumentsTerm } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Upgrade from 'modules/core/components/Upgrade'
import { UpgradeActions } from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import useLegalEntities from 'modules/expenses/hooks/useLegalEntities'
import {
  SignableOrganizationPerCountry,
  useTaxAgencyHomologations,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { UpsellingKit } from 'modules/upselling_kit/components'

import LegalComplianceAccessItem from './LegalComplianceAccessItem'
import LegalComplianceTermsItem from './LegalComplianceTermsItem'

interface LegalComplianceSectionProps {
  signedDocumentsTerms: ExpensesSignedDocumentsTerm[]
}

const LegalComplianceSection = ({ signedDocumentsTerms }: LegalComplianceSectionProps) => {
  const [showUpgradeModalFlow, setShowUpgradeModalFlow] = React.useState(false)
  const { legalEntities } = useLegalEntities()
  const {
    loading,
    enabled,
    organizations: { aeat, urssaf, ade },
  } = useTaxAgencyHomologations(legalEntities)
  const { company } = useCurrentSession()

  if (loading) return <Loading />

  const anyOrgApplies = [aeat, urssaf, ade].some((org) => org.applies)
  if (!anyOrgApplies) return null

  const hasTermsSigned = (organitzationId: SignableOrganizationPerCountry) =>
    signedDocumentsTerms.some((t) => t.organization == organitzationId)

  return (
    <>
      <Box flexGrow>
        <Row>
          <Row.Col halfWidth>
            <Title
              variant="400"
              title={i18n.t('expenses.settings.legal_compliance.title_v2')}
              description={i18n.t('expenses.settings.legal_compliance.description_v2')}
              illustration="office-sign-document"
            />
          </Row.Col>
          <Row.Col halfWidth>
            {!enabled && (
              <Box flexGrow justifyContent="center">
                <Upgrade.Small
                  title={i18n.t('expenses.settings.legal_compliance.upgrade_banner')}
                  action={{
                    type: UpgradeActions.knowMore,
                    onClick: () => setShowUpgradeModalFlow(true),
                  }}
                />
              </Box>
            )}
            {enabled && (
              <List>
                {aeat.applies && (
                  <List.Item key={aeat.id}>
                    {hasTermsSigned(aeat.id) ? (
                      <LegalComplianceAccessItem
                        enabled={enabled}
                        organization={aeat.id}
                        hasTermsSigned
                      />
                    ) : (
                      <LegalComplianceTermsItem enabled={enabled} organization={aeat.id} />
                    )}
                  </List.Item>
                )}
                {urssaf.applies && (
                  <List.Item key={urssaf.id}>
                    {hasTermsSigned(urssaf.id) ? (
                      <LegalComplianceAccessItem
                        enabled={enabled}
                        organization={urssaf.id}
                        hasTermsSigned
                      />
                    ) : (
                      <LegalComplianceTermsItem enabled={enabled} organization={urssaf.id} />
                    )}
                  </List.Item>
                )}
                {ade.applies && (
                  <List.Item key={ade.id}>
                    <LegalComplianceAccessItem
                      enabled={enabled}
                      organization={ade.id}
                      hasTermsSigned={hasTermsSigned(ade.id)}
                    />
                  </List.Item>
                )}
              </List>
            )}
          </Row.Col>
        </Row>
      </Box>

      {showUpgradeModalFlow && (
        <UpsellingKit.ProductModal
          isOpen={showUpgradeModalFlow}
          onClose={() => setShowUpgradeModalFlow(false)}
          requestSource="Expenses homologation settings"
          featureRequested={Features.expenses_homologation}
          moduleRequested={AvailableModules.expenses}
          requestReason={missingFeatureRequestReason(company)}
          pageSource="expenses_settings_legal_compliance"
        />
      )}
    </>
  )
}

export default observer(LegalComplianceSection)
